/**
 * FarmSettingsPanel (HOF-627) — the read/write half of Settings → Betrieb, mounted on open.
 *
 * - **Farm profile** (``/v1/farm/profile``): the farm's name lives on the farm (R6, farm-name
 *   cutover). ``can_edit`` (owners, farm managers) decides whether the fields are editable.
 * - **Switcher**: a person in several farms picks one; the BFF moves its session
 *   (``/session/active-farm``) and the page reloads into that farm.
 * - **Deletion** (``/v1/farm/deletion``, owners): typed-name confirmation AND a one-time code sent
 *   to the owner's own phone (``/v1/farm/deletion/challenge`` — §6 "fresh credential"), a 7-day
 *   grace period shown with its date, and a cancel while it is pending.
 *
 * Laid out with the design system's settings rows (settings refactor): the farm's details as one
 * form with one save, deletion in a danger-framed section at the end.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input, NativeSelect, SettingsRow, SettingsSection } from '@hofsync/design-system';
import api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import type { FarmSectionProps } from './FarmSection';

type FarmPanelProps = Omit<FarmSectionProps, 'backLink' | 'focusOnMount'>;
import { refusalCode } from './farmMembers';

interface FarmProfile {
    tenant_id: string;
    display_name: string | null;
    legal_name: string | null;
    region: string | null;
    holder_names: string[];
    identity_aliases: string[];
    status: string;
    can_edit: boolean;
}

interface DeletionState {
    status: string;
    requested_at: string | null;
    grace_until: string | null;
    checklist: Record<string, string> | null;
    can_request: boolean;
    /** A pending deletion still within its grace that this person may cancel (HOF-627, codex round 12). */
    can_cancel?: boolean;
}

const farmKeys = { profile: ['farm', 'profile'] as const, deletion: ['farm', 'deletion'] as const };
const opts = { withCredentials: true };

/**
 * With no active farm (HOF-627, codex round 11 — e.g. the farm one worked in is being deleted) there
 * is no farm profile to read: the panel only offers the person's other farms to choose from.
 */
export default function FarmSettingsPanel(props: FarmPanelProps) {
    return props.activeTenantId ? <ActiveFarmPanel {...props} /> : <FarmChooser {...props} />;
}

function FarmChooser({ farms, onSwitched }: FarmPanelProps) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const choose = useMutation({
        mutationFn: async (tenantId: string) => (await api.post('/session/active-farm', { tenant_id: tenantId }, opts)).data,
        onSuccess: onSwitched,
        onError: () => showToast(t('settings.farm.saveFailed', 'Die Änderung konnte nicht gespeichert werden.'), 'error'),
    });
    return (
        <SettingsSection>
            <SettingsRow label={t('settings.farm.choose', 'Betrieb wählen')} htmlFor="farm-chooser" field>
                <NativeSelect id="farm-chooser" value="" onChange={(e) => e.target.value && choose.mutate(e.target.value)}>
                    <option value="" disabled>
                        {t('settings.farm.chooseHint', 'Du arbeitest gerade in keinem Betrieb.')}
                    </option>
                    {farms.map((farm) => (
                        <option key={farm.tenant_id} value={farm.tenant_id}>
                            {farm.farm_name || farm.tenant_id}
                        </option>
                    ))}
                </NativeSelect>
            </SettingsRow>
        </SettingsSection>
    );
}

function splitList(value: string): string[] {
    return value
        .split(',')
        .map((name) => name.trim())
        .filter(Boolean);
}

type FarmForm = Record<'display_name' | 'legal_name' | 'region' | 'holder_names' | 'identity_aliases', string>;
type FarmProfileUpdate = Partial<Pick<FarmProfile, 'display_name' | 'legal_name' | 'region' | 'holder_names' | 'identity_aliases'>>;

/**
 * The fields this person changed, compared against the profile they loaded (release gate C1): the
 * PATCH is partial (``exclude_unset``), so an unchanged field is left out rather than written back
 * with the value read — two managers editing different fields never undo each other. A value
 * retyped to what was there (spacing, list separators) is not a change.
 */
function changedFarmFields(form: FarmForm, loaded: FarmProfile): FarmProfileUpdate {
    const changes: FarmProfileUpdate = {};
    for (const key of ['display_name', 'legal_name', 'region'] as const) {
        const value = form[key].trim();
        if (value !== (loaded[key] ?? '').trim()) changes[key] = value;
    }
    for (const key of ['holder_names', 'identity_aliases'] as const) {
        const value = splitList(form[key]);
        if (value.join('\n') !== splitList((loaded[key] ?? []).join(',')).join('\n')) changes[key] = value;
    }
    return changes;
}

function ActiveFarmPanel({ farms, activeTenantId, onSwitched }: FarmPanelProps) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    const { fetchUserProfile } = useAuth();
    const profile = useQuery<FarmProfile>({
        queryKey: farmKeys.profile,
        queryFn: async () => (await api.get<FarmProfile>('/v1/farm/profile', opts)).data,
    });
    const deletion = useQuery<DeletionState>({
        queryKey: farmKeys.deletion,
        queryFn: async () => (await api.get<DeletionState>('/v1/farm/deletion', opts)).data,
    });
    const [form, setForm] = useState<FarmForm>({ display_name: '', legal_name: '', region: '', holder_names: '', identity_aliases: '' });
    const [confirmName, setConfirmName] = useState('');
    const [codeSent, setCodeSent] = useState(false);
    const [code, setCode] = useState('');

    useEffect(() => {
        if (profile.data) {
            setForm({
                display_name: profile.data.display_name ?? '',
                legal_name: profile.data.legal_name ?? '',
                region: profile.data.region ?? '',
                holder_names: profile.data.holder_names.join(', '),
                identity_aliases: (profile.data.identity_aliases ?? []).join(', '),
            });
        }
    }, [profile.data]);

    const failed = () => showToast(t('settings.farm.saveFailed', 'Die Änderung konnte nicht gespeichert werden.'), 'error');
    const save = useMutation({
        // Only what changed (C1). ``identity_aliases`` are the spellings the farm's documents arrive
        // carrying; the worker reads them as asserted names (equality up to OCR noise), never looser.
        mutationFn: async (loaded: FarmProfile) =>
            (await api.patch('/v1/farm/profile', changedFarmFields(form, loaded), opts)).data,
        onSuccess: () => {
            showToast(t('settings.farm.saved', 'Betrieb gespeichert'), 'success');
            queryClient.invalidateQueries({ queryKey: farmKeys.profile });
            // The header and the farm switcher read the name from the auth profile (C3).
            void fetchUserProfile();
        },
        onError: failed,
    });
    const switchFarm = useMutation({
        mutationFn: async (tenantId: string) => (await api.post('/session/active-farm', { tenant_id: tenantId }, opts)).data,
        onSuccess: onSwitched,
        onError: failed,
    });
    const challenge = useMutation({
        mutationFn: async () => (await api.post('/v1/farm/deletion/challenge', {}, opts)).data,
        onSuccess: () => {
            setCodeSent(true);
            showToast(t('settings.farm.codeSent', 'Code gesendet. Er ist einige Minuten gültig.'), 'success');
        },
        onError: failed,
    });
    const requestDeletion = useMutation({
        mutationFn: async () =>
            (await api.post('/v1/farm/deletion', { confirm_farm_name: confirmName, code: code.trim() }, opts)).data,
        onSuccess: () => {
            setConfirmName('');
            setCode('');
            setCodeSent(false);
            queryClient.invalidateQueries({ queryKey: farmKeys.deletion });
        },
        onError: (error: unknown) =>
            refusalCode(error) === 'step_up_failed'
                ? showToast(t('settings.farm.codeWrong', 'Der Code ist falsch oder abgelaufen.'), 'error')
                : failed(),
    });
    const cancelDeletion = useMutation({
        mutationFn: async () => (await api.delete('/v1/farm/deletion', opts)).data,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: farmKeys.deletion }),
        onError: failed,
    });

    if (profile.isLoading) {
        return <p role="status">{t('settings.farm.loading', 'Wird geladen …')}</p>;
    }
    if (profile.isError || !profile.data) {
        return <p role="alert">{t('settings.farm.loadFailed', 'Der Betrieb konnte nicht geladen werden.')}</p>;
    }

    const editable = profile.data.can_edit;
    const field = (key: keyof typeof form, label: string) => (
        <SettingsRow label={label} htmlFor={`farm-${key}`} field>
            <Input
                id={`farm-${key}`}
                value={form[key]}
                disabled={!editable}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
            />
        </SettingsRow>
    );
    const state = deletion.data;
    const pending = state?.status === 'deletion_pending';
    const nameMatches = !!confirmName.trim() && confirmName.trim() === (profile.data.display_name ?? '').trim();

    return (
        <>
            {farms.length > 1 && (
                <SettingsSection title={t('settings.farm.switchTitle', 'Betrieb wechseln')}>
                    <SettingsRow label={t('settings.farm.active', 'Aktiver Betrieb')} htmlFor="farm-switcher" field>
                        <NativeSelect
                            id="farm-switcher"
                            value={activeTenantId ?? ''}
                            onChange={(e) => switchFarm.mutate(e.target.value)}
                        >
                            {farms.map((farm) => (
                                <option key={farm.tenant_id} value={farm.tenant_id}>
                                    {farm.farm_name || farm.tenant_id}
                                </option>
                            ))}
                        </NativeSelect>
                    </SettingsRow>
                </SettingsSection>
            )}
            <SettingsSection
                title={t('settings.farm.detailsTitle', 'Angaben')}
                description={t('settings.farm.hint', 'Der Betriebsname gehört dem Betrieb, nicht einer Person.')}
                action={
                    editable && (
                        <Button size="sm" disabled={save.isPending} onClick={() => profile.data && save.mutate(profile.data)}>
                            {t('settings.farm.save', 'Betrieb speichern')}
                        </Button>
                    )
                }
            >
                {field('display_name', t('settings.farm.displayName', 'Betriebsname'))}
                {field('legal_name', t('settings.farm.legalName', 'Rechtlicher Name'))}
                {field('region', t('settings.farm.region', 'Bundesland'))}
                {field('holder_names', t('settings.farm.holderNames', 'Kontoinhaber (durch Komma getrennt)'))}
                {field(
                    'identity_aliases',
                    t('settings.farm.identityAliases', 'Andere Schreibweisen des Betriebs (durch Komma getrennt)')
                )}
            </SettingsSection>
            {(pending || state?.can_request) && (
                <SettingsSection title={t('settings.farm.deleteTitle', 'Betrieb löschen')} tone="danger">
                    {pending ? (
                        <SettingsRow
                            label={
                                <span role="status">
                                    {t('settings.farm.deletionPending', 'Löschung geplant für {{date}}', {
                                        date: state?.grace_until ? new Date(state.grace_until).toLocaleDateString() : '—',
                                    })}
                                </span>
                            }
                            description={
                                !state?.can_cancel && state?.can_request
                                    ? t(
                                          'settings.farm.graceOver',
                                          'Die Frist ist abgelaufen; die Löschung kann nicht mehr abgebrochen werden.'
                                      )
                                    : undefined
                            }
                        >
                            {state?.can_cancel && (
                                <Button size="sm" variant="outline" onClick={() => cancelDeletion.mutate()}>
                                    {t('settings.farm.cancelDeletion', 'Löschung abbrechen')}
                                </Button>
                            )}
                        </SettingsRow>
                    ) : (
                        <>
                            <SettingsRow
                                label={t('settings.farm.deleteConfirm', 'Zur Bestätigung den Betriebsnamen eingeben')}
                                description={t(
                                    'settings.farm.deleteHint',
                                    'Nach 7 Tagen werden der Betrieb und alle seine Daten gelöscht. Bis dahin kannst du die Löschung abbrechen.'
                                )}
                                htmlFor="farm-delete-confirm"
                                stacked
                            >
                                <Input id="farm-delete-confirm" value={confirmName} onChange={(e) => setConfirmName(e.target.value)} />
                                {!codeSent && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={!nameMatches || challenge.isPending}
                                        onClick={() => challenge.mutate()}
                                    >
                                        {t('settings.farm.requestCode', 'Code anfordern')}
                                    </Button>
                                )}
                            </SettingsRow>
                            {codeSent && (
                                <SettingsRow label={t('settings.farm.code', 'Bestätigungscode')} htmlFor="farm-delete-code" stacked>
                                    <Input
                                        id="farm-delete-code"
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value)}
                                    />
                                    <Button
                                        size="sm"
                                        variant="destructive"
                                        disabled={!nameMatches || !code.trim() || requestDeletion.isPending}
                                        onClick={() => requestDeletion.mutate()}
                                    >
                                        {t('settings.farm.delete', 'Betrieb löschen')}
                                    </Button>
                                </SettingsRow>
                            )}
                        </>
                    )}
                </SettingsSection>
            )}
        </>
    );
}
